import { useMutation } from '@apollo/client';
import { EmployeeAvatar, IconRemove } from '@newfront-insurance/core-ui';
import { useNotify } from '@newfront-insurance/next-notifications';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AddPeopleDialog } from './add-people-dialog';
import { getEmployeeDisplayName, getEmployeeInitials } from './employee-utils';
import { useAddCustomSkillShares, useCustomSkillShares } from './use-custom-skill-shares';
import type { CustomSkillRecord, CustomSkillShareRecord } from '../../../../../queries/chats';
import { UNSHARE_CUSTOM_SKILL_FROM_USER } from '../../../../../queries/mutations';
import { getErrorMessage, getRootCustomSkillUuid } from '../custom-skill-model';
import { InlineIconButton } from '../inline-icon-button';

export function CustomSkillSharesSummary({
  skill,
  isAddingPeople,
  onAddPeople,
  onCancelAddPeople,
  onChanged,
}: {
  skill: CustomSkillRecord;
  isAddingPeople: boolean;
  onAddPeople: () => void;
  onCancelAddPeople: () => void;
  onChanged: () => Promise<void>;
}) {
  const notify = useNotify();
  const [unshareCustomSkillFromUser] = useMutation(UNSHARE_CUSTOM_SKILL_FROM_USER);
  const { shares, sharedEmployeesByUserUuid, isLoading, reloadShares } = useCustomSkillShares(skill);
  const [pendingRemoveUserUuid, setPendingRemoveUserUuid] = useState<string | null>(null);
  const rootSkillUuid = getRootCustomSkillUuid(skill);
  const excludedShareUserUuids = useMemo(() => new Set(shares.map((share) => share.userUuid)), [shares]);
  const handleAddSelectedPeople = useAddCustomSkillShares({ skill, reloadShares, onChanged });
  const shareCountLabel =
    shares.length === 0 ? 'Not shared' : `Shared with ${shares.length} ${shares.length === 1 ? 'person' : 'people'}`;

  const handleRemoveShare = async (share: CustomSkillShareRecord) => {
    setPendingRemoveUserUuid(share.userUuid);
    try {
      await unshareCustomSkillFromUser({ variables: { uuid: rootSkillUuid, userUuid: share.userUuid } });
      await reloadShares();
      await onChanged();
      notify.success('Custom skill access removed.');
    } catch (removeError) {
      notify.error(getErrorMessage(removeError, 'Unable to remove custom skill access.'));
    } finally {
      setPendingRemoveUserUuid(null);
    }
  };

  return (
    <section className="rounded-[4px] border border-[#DEE2E7] bg-white">
      <div className="flex min-h-9 items-center justify-between gap-3 border-b border-[#DEE2E7] bg-[#FAFBFC] px-[14px] py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#262D46]">
            {shareCountLabel}
          </h3>
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-[#546A83]" /> : null}
        </div>
        <button
          type="button"
          className="shrink-0 font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#0957C3]"
          onClick={onAddPeople}
        >
          + Add people
        </button>
      </div>

      <AddPeopleDialog
        open={isAddingPeople}
        excludedUserUuids={excludedShareUserUuids}
        onClose={onCancelAddPeople}
        onAddPeople={handleAddSelectedPeople}
      />

      {shares.length === 0 && !isLoading ? (
        <p className="px-[14px] py-4 font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#546A83]">
          Only you can use this skill right now.
        </p>
      ) : (
        shares.map((share, index) => {
          const sharedEmployee = sharedEmployeesByUserUuid.get(share.userUuid);
          const sharedEmployeeName = sharedEmployee ? getEmployeeDisplayName(sharedEmployee) : null;
          const isRemovingShare = pendingRemoveUserUuid === share.userUuid;

          return (
            <div
              key={share.userUuid}
              className={`flex min-h-[52px] items-center gap-3 border-b border-[#DEE2E7] px-[14px] py-2 last:border-b-0 ${
                index % 2 === 1 ? 'bg-[#FAFBFC]' : 'bg-white'
              }`}
            >
              {sharedEmployee ? (
                <>
                  <EmployeeAvatar
                    sizeInPixels={36}
                    imageUrl={sharedEmployee.imageUrl}
                    initials={getEmployeeInitials(sharedEmployee, sharedEmployeeName ?? '')}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#262D46]">
                      {sharedEmployeeName}
                    </p>
                    {sharedEmployee.email ? (
                      <p className="truncate font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
                        {sharedEmployee.email}
                      </p>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  <div
                    aria-label="Loading shared user"
                    className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-[#E2EBF1]"
                  />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="h-4 w-32 animate-pulse rounded bg-[#E2EBF1]" />
                    <div className="h-3 w-44 max-w-full animate-pulse rounded bg-[#E2EBF1]" />
                  </div>
                </>
              )}
              <InlineIconButton
                label={sharedEmployeeName ? `Remove ${sharedEmployeeName}` : 'Remove shared user'}
                tooltip="Unshare"
                disabled={Boolean(pendingRemoveUserUuid)}
                ariaBusy={isRemovingShare}
                onClick={() => void handleRemoveShare(share)}
              >
                {isRemovingShare ? (
                  <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                ) : (
                  <span aria-hidden="true">
                    <IconRemove color="currentColor" size={16} />
                  </span>
                )}
              </InlineIconButton>
            </div>
          );
        })
      )}
    </section>
  );
}
